import React, { useState, useEffect } from 'react';
import { X, Download, Share, Plus, Smartphone } from 'lucide-react';

export default function PWAInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                       window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if prompt was dismissed recently
    const dismissedAt = localStorage.getItem('pwa_prompt_dismissed');
    if (dismissedAt) {
      const dismissedTime = new Date(dismissedAt).getTime();
      const now = new Date().getTime();
      const daysSinceDismissed = (now - dismissedTime) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return; // Don't show for 7 days after dismissal
      }
    }

    // Listen for beforeinstallprompt event (Android/Desktop Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a short delay
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS, show custom prompt after delay
    if (iOS && !standalone) {
      setTimeout(() => setShowPrompt(true), 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        // console.log('User accepted the install prompt');
      }
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa_prompt_dismissed', new Date().toISOString());
  };

  // Don't show if already installed
  if (isStandalone || !showPrompt) {
    return null;
  }

  // iOS Install Instructions
  if (isIOS) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up" data-testid="pwa-install-prompt">
        <div className="bg-white  shadow-2xl border border-white/40 p-5 max-w-md mx-auto">
          <button 
            onClick={handleDismiss}
            className="absolute top-3 right-3 p-1 text-neutral-400 hover:text-neutral-600"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-[#FF3366] to-[#FF3366] rounded-xl flex items-center justify-center">
              <Smartphone className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-[#1a1a2e]">Install Girgit</h3>
              <p className="text-sm text-neutral-500">Add to your home screen</p>
            </div>
          </div>

          <div className="bg-white/40 rounded-xl p-4 space-y-3">
            <p className="text-sm text-neutral-700 font-medium">To install this app:</p>
            <div className="flex items-center gap-3 text-sm text-neutral-600">
              <div className="w-8 h-8 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Share className="w-4 h-4 text-blue-600" />
              </div>
              <span>Tap the <strong>Share</strong> button in Safari</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-neutral-600">
              <div className="w-8 h-8 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Plus className="w-4 h-4 text-green-600" />
              </div>
              <span>Tap <strong>&quot;Add to Home Screen&quot;</strong></span>
            </div>
          </div>

          <button
            onClick={handleDismiss}
            className="w-full mt-4 py-3 text-neutral-600 font-medium text-sm hover:text-[#1a1a2e] transition-colors"
          >
            Maybe Later
          </button>
        </div>
      </div>
    );
  }

  // Android/Desktop Install Button
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up" data-testid="pwa-install-prompt">
      <div className="bg-white  shadow-2xl border border-white/40 p-5 max-w-md mx-auto">
        <button 
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1 text-neutral-400 hover:text-neutral-600"
          aria-label="Dismiss"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-[#FF3366] to-[#FF3366] rounded-xl flex items-center justify-center">
            <Download className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-[#1a1a2e]">Install Girgit App</h3>
            <p className="text-sm text-neutral-500">Get the full app experience</p>
          </div>
        </div>

        <ul className="text-sm text-neutral-600 space-y-2 mb-4">
          <li className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-pink-500 rounded-full"></span>
            Quick access from home screen
          </li>
          <li className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-pink-500 rounded-full"></span>
            Works offline
          </li>
          <li className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-pink-500 rounded-full"></span>
            Faster performance
          </li>
        </ul>

        <div className="flex gap-3">
          <button
            onClick={handleDismiss}
            className="flex-1 py-3 text-neutral-600 font-medium text-sm hover:text-[#1a1a2e] transition-colors border border-white/40 rounded-xl"
          >
            Not Now
          </button>
          <button
            onClick={handleInstall}
            className="flex-1 py-3 bg-gradient-to-br from-[#FF3366] to-[#FF3366] text-white font-semibold rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" />
            Install
          </button>
        </div>
      </div>
    </div>
  );
}
